/**
 * ChartVizResult — Chart Visualization result renderer.
 *
 * Spec: v2-design/RESULT_RENDERING_v0_spec.md §3.
 *
 * Renders a single chart per spec §11 (no multi-tab chart1/chart2
 * scaffolding).
 *
 * Input is the verbatim backend chart_payload (whatever the
 * /Chart_Visualization API returned), enriched server-side with a
 * `request_context` block carrying ModelInstanceID + RowIdentifierIDs +
 * Timescales — the IDs the FE needs to fetch RowIdentifier formatting
 * info. That fetch stays client-side per spec §3.4's chosen v0 path,
 * rather than having the backend enrich the payload directly.
 *
 * Sizing: the card body grows to fit the chart + legend + axis labels.
 * Spec §3.5 originally called for a fixed 360px height with internal
 * scroll, but the legend below the chart pushed past that bound in
 * practice. Auto height is the v0 choice; revisit if a single result
 * card visually dwarfs surrounding chat content.
 */
import React, { useCallback, useState } from 'react';
import { Box } from '@mui/material';
import { find, includes } from 'lodash';

import { dateFormats } from 'src/components/ScenarioManager/EditAssumptionsDetail/OutputsPanel/FormatChartPanel';
import {
  formatParameters,
  IFormatOutputParameters,
} from 'src/components/Visualization/formatParameters';
import { useData } from 'src/hooks/useData';
import { RowIdentifier, Timescale } from 'src/types/models';

import GenerateChartPanel, {
  chartTypes,
  chartDataTypes,
} from './charts/GenerateChartPanel';

interface ChartDataSeries {
  id?: number;
  scenario?: string;
  segment?: string;
  name?: string;
  values?: number[];
  visible?: boolean;
  output?: string;
}

interface ChartDataBlock {
  PreviewData?: ChartDataSeries[];
  PublishedData?: ChartDataSeries[];
  Dates?: Array<{ Date: string }>;
  ChartName?: string;
}

interface ChartPayload {
  // The forecast API wraps its chart response under `data`, so
  // chart_payload.data holds {PreviewData, PublishedData, Dates, ChartName}.
  data?: ChartDataBlock;
  Status?: unknown;
  // Server-side bundle of the IDs the FE needs to look up RowIdentifier
  // format metadata. Optional for compatibility — the renderer falls
  // back to default formatting when absent.
  request_context?: {
    model_instance_id?: number;
    row_identifier_ids?: number[];
    timescales?: string[];
  };
}

export interface ChartVizResultProps {
  chartPayload: ChartPayload;
  // When false, suppresses the per-scenario legend. v0 default true.
  showScenarios?: boolean;
  // Exec chart frame (2026-09-07): forwarded to GenerateChartPanel — see its prop comment.
  frameChart?: boolean;
}

const transformSeries = (
  series: ChartDataSeries[] | undefined
): ChartDataSeries[] =>
  (series ?? []).map((item) => ({ ...item, visible: true }));

export const ChartVizResult: React.FC<ChartVizResultProps> = ({
  chartPayload,
  showScenarios = true,
  frameChart,
}) => {
  const dataBlock = chartPayload.data ?? {};

  // Guard against empty/malformed payloads that would crash
  // GenerateChartPanel's internals. Done in the outer component so the
  // hook calls inside ChartVizInner aren't conditional.
  const hasDates = (dataBlock.Dates?.length ?? 0) > 0;
  const hasSeries =
    (dataBlock.PreviewData?.length ?? 0) > 0 ||
    (dataBlock.PublishedData?.length ?? 0) > 0;
  if (!hasDates || !hasSeries) {
    return (
      <Box
        sx={{
          height: 240,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'text.secondary',
          fontSize: 13,
          textAlign: 'center',
          px: 2,
        }}
      >
        <Box sx={{ fontWeight: 600, mb: 0.5 }}>Chart data unavailable</Box>
        <Box sx={{ fontSize: 12 }}>
          The backend returned a chart payload without data series or dates.
          Check the Agentic-API logs and the Chart Visualization API response
          for the active plan.
        </Box>
      </Box>
    );
  }

  return (
    <ChartVizInner
      chartPayload={chartPayload}
      showScenarios={showScenarios}
      frameChart={frameChart}
    />
  );
};

const ChartVizInner: React.FC<
  Required<Pick<ChartVizResultProps, 'chartPayload'>> & {
    showScenarios: boolean;
    frameChart?: boolean;
  }
> = ({ chartPayload, showScenarios, frameChart }) => {
  const dataBlock = chartPayload.data ?? {};
  const modelInstanceId = chartPayload.request_context?.model_instance_id;
  const rowIdentifierIds = chartPayload.request_context?.row_identifier_ids;
  const timescaleString = chartPayload.request_context?.timescales?.[0];

  // Local interactive state for the chart panel (range / type / data type
  // / legend visibility). Always single-chart in v0, so this is a flat
  // shape rather than per-tab state.
  const [chartData, setChartData] = useState<ChartDataBlock>({
    PreviewData: transformSeries(dataBlock.PreviewData),
    PublishedData: transformSeries(dataBlock.PublishedData),
    Dates: dataBlock.Dates ?? [],
  });
  const refreshedChartData = chartData; // v0 — no separate refresh source

  const [selectedChartRange, setSelectedChartRange] = useState<
    [string, string] | null
  >(null);
  const [selectedChartType, setSelectedChartType] = useState<string>(
    chartTypes[0].id
  );
  const [selectedChartDataType, setSelectedChartDataType] = useState<string>(
    chartDataTypes[0].id
  );

  // Fetches the model's full RowIdentifier set; narrowed below to the
  // one matching our payload's Output RowIdentifier ID, to derive
  // formatKey + y-axis title.
  const { data } = useData<{ rowIdentifiers?: RowIdentifier[] }>(
    () => ({
      rowIdentifiers: modelInstanceId
        ? `/instances/${modelInstanceId}/row_identifiers`
        : undefined,
    }),
    [modelInstanceId]
  );

  const selectedRowIdentifier = React.useMemo(() => {
    if (!data?.rowIdentifiers || !rowIdentifierIds) return undefined;
    return data.rowIdentifiers.find(
      (r) => rowIdentifierIds.includes(r.id) && r.Type === 'Output'
    );
  }, [data?.rowIdentifiers, rowIdentifierIds]);

  const formatKey = selectedRowIdentifier?.FormatKey ?? 0;
  const formatSpec = find(formatParameters, { formatKey }) as
    | IFormatOutputParameters
    | undefined;
  const selectedPrefixValue = formatSpec?.numberType === '$' ? '$' : '';
  const selectedSuffixValue = formatSpec?.numberType === 'Percent' ? '%' : '';
  const magnitudeTruncation = (formatSpec?.magnitudeTruncation as number) ?? 0;

  const yAxisTitle =
    selectedRowIdentifier?.Name ?? dataBlock.PublishedData?.[0]?.output ?? '';

  const handleChangeChartRange = useCallback(
    (startRange: string, endRange: string) => {
      setSelectedChartRange([startRange, endRange]);
    },
    []
  );

  const handleChangeChartType = useCallback((next: string) => {
    setSelectedChartType(next);
  }, []);

  const handleChangeChartDataType = useCallback((next: string) => {
    setSelectedChartDataType(next);
  }, []);

  const handleLegendChange = useCallback((id: number, visible: boolean) => {
    setChartData((prev) => ({
      Dates: prev.Dates,
      PublishedData: (prev.PublishedData ?? []).map((item) =>
        item.id === id ? { ...item, visible } : item
      ),
      PreviewData: (prev.PreviewData ?? []).map((item) =>
        item.id === id ? { ...item, visible } : item
      ),
    }));
  }, []);

  return (
    <Box sx={{ position: 'relative' }}>
      <ChartVizErrorBoundary>
        <GenerateChartPanel
          chartData={chartData as never}
          refreshedChartData={refreshedChartData as never}
          timescale={(timescaleString as Timescale) ?? Timescale.Yearly}
          formatKey={formatKey}
          yAxisTitle={yAxisTitle}
          chartTitle={dataBlock.ChartName ?? ''}
          prefix={selectedPrefixValue}
          suffix={selectedSuffixValue}
          magnitudeTruncation={magnitudeTruncation}
          decimalPlaces={2}
          chartDateType={
            includes(
              [Timescale.Weekly, Timescale.Monthly],
              timescaleString as Timescale
            )
              ? dateFormats(timescaleString as Timescale)[0] ?? ''
              : ''
          }
          selectedChartRange={selectedChartRange}
          selectedChartType={selectedChartType}
          selectedChartDataType={selectedChartDataType}
          handleChangeChartRange={handleChangeChartRange}
          handleChangeChartType={handleChangeChartType}
          handleChangeChartDataType={handleChangeChartDataType}
          handleLegendChange={handleLegendChange}
          isPanelCollapsed={false}
          showScenarios={showScenarios}
          frameChart={frameChart}
        />
      </ChartVizErrorBoundary>
    </Box>
  );
};

/**
 * Local error boundary so a crash inside GenerateChartPanel doesn't
 * propagate up to the app-wide ErrorBoundary in App.tsx — which would
 * replace the entire app (header, nav, everything) with "Something
 * went wrong." Contains the failure to this one chart result instead,
 * so the rest of the chat and app stay usable when a payload is
 * malformed.
 */
class ChartVizErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { error: Error | null }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error('[ChartVizResult] render failed', error, info);
  }

  render() {
    if (this.state.error) {
      return (
        <Box
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'text.secondary',
            fontSize: 13,
            textAlign: 'center',
            px: 2,
          }}
        >
          <Box sx={{ fontWeight: 600, mb: 0.5 }}>Chart render failed</Box>
          <Box sx={{ fontSize: 12 }}>
            {this.state.error.message}. See the console for the full error and
            the [ChartVizResult] payload log.
          </Box>
        </Box>
      );
    }
    return this.props.children;
  }
}
